import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { institutionsAPI, plansAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, 
  Pencil, 
  Buildings,
  MagnifyingGlass,
  Spinner,
  WarningCircle,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Envelope,
  Users,
  Gear,
  Package,
  ClockCounterClockwise,
  ArrowRight,
  Minus,
  Globe,
  Palette
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const INSTITUTION_TYPES = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'clinica', label: 'Clínica' },
  { value: 'universidad', label: 'Universidad' },
  { value: 'cesfam', label: 'CESFAM' },
  { value: 'instituto', label: 'Instituto' },
  { value: 'otro', label: 'Otro' },
];

const REGIONS = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana', 'O\'Higgins', 'Maule', 'Ñuble',
  'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén', 'Magallanes'
];

const defaultInstitution = {
  nombre: '',
  tipo: 'universidad',
  region: 'Metropolitana',
  comuna: '',
  direccion: '',
  telefono: '',
  email_contacto: '',
  settings: {
    color_primario: '#2563EB',
    color_secundario: '#1E40AF',
    max_usuarios: 100,
    max_simulaciones_mes: 1000,
    permite_crear_escenarios: true
  }
};

export default function EstablishmentsPage() {
  const { isSuperAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingInst, setEditingInst] = useState(null);
  const [formData, setFormData] = useState(defaultInstitution);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({});
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedModalidad, setSelectedModalidad] = useState('mensual');
  const [selectedSubStatus, setSelectedSubStatus] = useState('activa');
  const [currentPlanInfo, setCurrentPlanInfo] = useState(null); // { id, nombre, codigo, color, status, modalidad }
  const [loadingPlanForm, setLoadingPlanForm] = useState(false);
  const [planHistory, setPlanHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const canCreate = isSuperAdmin || hasPermission('establecimientos', 'crear');
  const canEdit = isSuperAdmin || hasPermission('establecimientos', 'editar');
  const canManagePlan = isSuperAdmin;

  useEffect(() => {
    loadInstitutions();
    if (canManagePlan) {
      loadPlans();
    }
  }, []);

  const loadPlans = async () => {
    try {
      const response = await plansAPI.getAll();
      setPlans(response.data || []);
    } catch (error) {
      // Non-fatal: user can still create institution without a plan
      console.warn('No se pudieron cargar los planes', error);
    }
  };

  const loadInstitutions = async () => {
    try {
      const response = await institutionsAPI.getAll();
      // Normalize institutions to handle legacy schema (name/type vs nombre/tipo)
      const normalized = (response.data || []).map(inst => ({
        ...inst,
        nombre: inst.nombre || inst.name || '',
        tipo: inst.tipo || inst.type || 'otro',
        region: inst.region || '',
        comuna: inst.comuna || '',
        telefono: inst.telefono || inst.contact_phone || '',
        email_contacto: inst.email_contacto || inst.contact_email || '',
        settings: inst.settings || {},
      }));
      setInstitutions(normalized);
      
      // Calculate stats
      const total = normalized.length;
      const activos = normalized.filter(i => i.is_active).length;
      const byType = normalized.reduce((acc, i) => {
        acc[i.tipo] = (acc[i.tipo] || 0) + 1;
        return acc;
      }, {});
      
      setStats({ total, activos, byType });
    } catch (error) {
      toast.error('Error al cargar establecimientos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (inst = null) => {
    // Reset plan state
    setSelectedPlanId('');
    setSelectedModalidad('mensual');
    setSelectedSubStatus('activa');
    setCurrentPlanInfo(null);
    setPlanHistory([]);
    setShowHistory(false);

    if (inst) {
      setEditingInst(inst);
      setFormData({
        ...defaultInstitution,
        ...inst,
        settings: { ...defaultInstitution.settings, ...inst.settings }
      });
      // Load subscription for this institution
      if (canManagePlan) {
        try {
          setLoadingPlanForm(true);
          const [subResp, histResp] = await Promise.all([
            institutionsAPI.getSubscription(inst.id),
            institutionsAPI.getSubscriptionHistory(inst.id).catch(() => ({ data: { history: [] } })),
          ]);
          const sub = subResp.data?.subscription;
          const plan = subResp.data?.plan;
          if (sub) {
            setSelectedPlanId(sub.plan_id || '');
            setSelectedModalidad(sub.modalidad || 'mensual');
            setSelectedSubStatus(sub.status || 'activa');
            setCurrentPlanInfo({
              id: sub.plan_id,
              nombre: plan?.nombre,
              codigo: plan?.codigo,
              color: plan?.color,
              status: sub.status,
              modalidad: sub.modalidad,
            });
          }
          setPlanHistory(histResp.data?.history || []);
        } catch (e) {
          // ignore
        } finally {
          setLoadingPlanForm(false);
        }
      }
    } else {
      setEditingInst(null);
      setFormData(defaultInstitution);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingInst(null);
    setFormData(defaultInstitution);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSettingsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.region) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      let institutionId = editingInst?.id;
      if (editingInst) {
        await institutionsAPI.update(editingInst.id, formData);
      } else {
        const resp = await institutionsAPI.create(formData);
        institutionId = resp.data?.id;
      }

      // If super admin selected/changed the plan, assign it
      if (canManagePlan && selectedPlanId && institutionId) {
        const currentPlanId = currentPlanInfo?.id;
        const currentModalidad = currentPlanInfo?.modalidad;
        const currentStatus = currentPlanInfo?.status;
        const changed = (
          selectedPlanId !== currentPlanId ||
          selectedModalidad !== currentModalidad ||
          selectedSubStatus !== currentStatus
        );
        if (changed) {
          try {
            await institutionsAPI.assignPlan(institutionId, {
              plan_id: selectedPlanId,
              modalidad: selectedModalidad,
              status: selectedSubStatus,
            });
          } catch (planErr) {
            toast.error(planErr.response?.data?.detail || 'Error al asignar plan');
          }
        }
      }

      toast.success(editingInst ? 'Establecimiento actualizado' : 'Establecimiento creado');
      handleCloseModal();
      loadInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (inst) => {
    try {
      await institutionsAPI.toggleActive(inst.id);
      toast.success(inst.is_active ? 'Establecimiento desactivado' : 'Establecimiento activado');
      loadInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const filteredInstitutions = institutions.filter(inst => {
    const nombre = inst.nombre || inst.name || '';
    const region = inst.region || '';
    const tipo = inst.tipo || inst.type;
    const term = searchTerm.toLowerCase();
    const matchesSearch = nombre.toLowerCase().includes(term) ||
                         region.toLowerCase().includes(term);
    const matchesType = selectedType === 'all' || tipo === selectedType;
    return matchesSearch && matchesType;
  });

  const getTypeLabel = (type) => {
    return INSTITUTION_TYPES.find(t => t.value === type)?.label || type;
  };

  // -------- Plan comparator helpers --------
  const LIMIT_LABELS = {
    max_usuarios_total: 'Usuarios totales',
    max_alumnos: 'Alumnos',
    max_instructores: 'Instructores',
    max_administradores: 'Administradores',
    max_establecimientos: 'Establecimientos',
    max_escenarios: 'Escenarios',
    max_simulaciones_mes: 'Simulaciones/mes',
    max_almacenamiento_gb: 'Almacenamiento (GB)',
    max_dominios: 'Dominios personalizados',
  };
  const MODULE_LABELS = {
    dashboard: 'Dashboard',
    biblioteca_escenarios: 'Biblioteca de escenarios',
    simulaciones_interactivas: 'Simulaciones interactivas',
    monitor_signos_vitales: 'Monitor de signos vitales',
    supervision_vivo: 'Supervisión en vivo',
    debriefing: 'Debriefing',
    protocolos: 'Protocolos clínicos',
    cursos: 'Cursos',
    evaluaciones_osce: 'Evaluaciones OSCE',
    branding: 'Branding personalizado',
    api_access: 'Acceso API',
  };
  const formatLimit = (limit) => {
    if (!limit) return '—';
    if (limit.tipo === 'ilimitado') return 'Ilimitado';
    if (limit.tipo === 'no_disponible') return 'No disponible';
    return String(limit.valor ?? '—');
  };
  const isModuleIncluded = (mod) => mod && (mod.status === 'incluido' || mod.status === 'con_limite');
  const buildComparison = () => {
    if (!selectedPlanId) return null;
    const newPlan = plans.find(p => p.id === selectedPlanId);
    const currentPlan = plans.find(p => p.id === currentPlanInfo?.id);
    if (!newPlan) return null;
    // Modules diff
    const moduleAdded = [];
    const moduleRemoved = [];
    const allModuleKeys = new Set([
      ...Object.keys(newPlan.modulos || {}),
      ...Object.keys(currentPlan?.modulos || {}),
    ]);
    allModuleKeys.forEach(k => {
      const newIn = isModuleIncluded(newPlan.modulos?.[k]);
      const oldIn = currentPlan ? isModuleIncluded(currentPlan.modulos?.[k]) : false;
      if (newIn && !oldIn) moduleAdded.push(k);
      if (!newIn && oldIn) moduleRemoved.push(k);
    });
    // Limit diffs
    const limitDiffs = [];
    const allLimitKeys = new Set([
      ...Object.keys(newPlan.limites || {}),
      ...Object.keys(currentPlan?.limites || {}),
    ]);
    allLimitKeys.forEach(k => {
      const newLim = newPlan.limites?.[k];
      const oldLim = currentPlan?.limites?.[k];
      const newStr = formatLimit(newLim);
      const oldStr = formatLimit(oldLim);
      if (newStr !== oldStr) {
        limitDiffs.push({ key: k, from: oldStr, to: newStr });
      }
    });
    return {
      isNewAssignment: !currentPlan,
      newPlan,
      currentPlan,
      moduleAdded,
      moduleRemoved,
      limitDiffs,
    };
  };
  const comparison = buildComparison();
  const planChanged = selectedPlanId && selectedPlanId !== currentPlanInfo?.id;
  // -----------------------------------------

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="establishments-title">
              Establecimientos
            </h1>
            <p className="text-slate-500">
              Gestión de instituciones del sistema
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-establishment-btn">
              <Plus size={18} />
              Nuevo Establecimiento
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <Buildings size={24} className="mx-auto text-slate-400 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-green-600">{stats.activos || 0}</p>
              <p className="text-xs text-slate-500">Activos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Buildings size={24} className="mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-blue-600">{stats.byType?.universidad || 0}</p>
              <p className="text-xs text-slate-500">Universidades</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Buildings size={24} className="mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold text-purple-600">{stats.byType?.hospital || 0}</p>
              <p className="text-xs text-slate-500">Hospitales</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar establecimientos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-full md:w-48" data-testid="type-filter">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {INSTITUTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Institutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInstitutions.map(inst => (
            <Card key={inst.id} className={`hover:shadow-md transition-shadow ${!inst.is_active ? 'opacity-60' : ''}`} data-testid={`institution-card-${inst.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg" style={{ backgroundColor: inst.settings?.color_primario + '20' }}>
                      <Buildings size={24} style={{ color: inst.settings?.color_primario || '#2563EB' }} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{inst.nombre}</CardTitle>
                      <Badge variant="secondary" className="mt-1">{getTypeLabel(inst.tipo)}</Badge>
                    </div>
                  </div>
                  {canEdit && (
                    <Button variant="ghost" size="sm" onClick={() => handleOpenModal(inst)} data-testid={`edit-${inst.id}`}>
                      <Pencil size={16} />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {inst.plan && (
                  <div className="flex items-center gap-2 text-slate-600" data-testid={`inst-plan-${inst.id}`}>
                    <Package size={14} />
                    <span className="font-medium">{inst.plan.nombre}</span>
                    {inst.plan.status && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                        {inst.plan.status}
                      </Badge>
                    )}
                  </div>
                )}
                {!inst.plan && (
                  <div className="flex items-center gap-2 text-slate-400 text-xs italic" data-testid={`inst-no-plan-${inst.id}`}>
                    <Package size={14} />
                    <span>Sin plan asignado</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin size={14} />
                  <span>{inst.region}{inst.comuna ? `, ${inst.comuna}` : ''}</span>
                </div>
                {inst.telefono && (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Phone size={14} />
                    <span>{inst.telefono}</span>
                  </div>
                )}
                {inst.email_contacto && (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Envelope size={14} />
                    <span className="truncate">{inst.email_contacto}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Users size={14} />
                    <span>Max: {inst.settings?.max_usuarios || 100}</span>
                  </div>
                  {inst.is_active ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle size={12} className="mr-1" weight="fill" />
                      Activo
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600">
                      <XCircle size={12} className="mr-1" weight="fill" />
                      Inactivo
                    </Badge>
                  )}
                </div>
                {/* Quick actions to reach establishment settings */}
                <div className="grid grid-cols-3 gap-1 pt-2" data-testid={`inst-actions-${inst.id}`}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1"
                    onClick={() => navigate(`/app/admin/establishment-settings?institution_id=${inst.id}&tab=dominio`)}
                    data-testid={`configure-domain-${inst.id}`}
                    title="Configurar dominio"
                  >
                    <Globe size={13} />
                    Dominio
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1"
                    onClick={() => navigate(`/app/admin/establishment-settings?institution_id=${inst.id}&tab=branding`)}
                    data-testid={`configure-branding-${inst.id}`}
                    title="Configurar branding"
                  >
                    <Palette size={13} />
                    Branding
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1"
                    onClick={() => navigate(`/app/admin/establishment-settings?institution_id=${inst.id}&tab=usuarios`)}
                    data-testid={`configure-users-${inst.id}`}
                    title="Cupos por rol (alumnos, instructores, admins)"
                  >
                    <Users size={13} />
                    Cupos
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredInstitutions.length === 0 && (
          <Card className="p-8 text-center">
            <WarningCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No se encontraron establecimientos</p>
          </Card>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-lg max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{editingInst ? 'Editar Establecimiento' : 'Nuevo Establecimiento'}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Nombre del establecimiento"
                    data-testid="inst-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={formData.tipo} onValueChange={(v) => handleChange('tipo', v)}>
                      <SelectTrigger data-testid="inst-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INSTITUTION_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Región *</Label>
                    <Select value={formData.region} onValueChange={(v) => handleChange('region', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map(region => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="comuna">Comuna</Label>
                    <Input
                      id="comuna"
                      value={formData.comuna}
                      onChange={(e) => handleChange('comuna', e.target.value)}
                      placeholder="Comuna"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefono">Teléfono</Label>
                    <Input
                      id="telefono"
                      value={formData.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="+56 9 1234 5678"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="direccion">Dirección</Label>
                  <Input
                    id="direccion"
                    value={formData.direccion}
                    onChange={(e) => handleChange('direccion', e.target.value)}
                    placeholder="Dirección completa"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email de contacto</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email_contacto}
                    onChange={(e) => handleChange('email_contacto', e.target.value)}
                    placeholder="contacto@institucion.cl"
                  />
                </div>

                {/* Plan Section - Solo Super Admin */}
                {canManagePlan && (
                  <div className="border-t pt-4 mt-4" data-testid="establishment-plan-section">
                    <h3 className="font-medium flex items-center gap-2 mb-4">
                      <Package size={18} />
                      Plan SaaS
                    </h3>

                    {loadingPlanForm ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Spinner size={16} className="animate-spin" />
                        Cargando plan actual...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {currentPlanInfo?.id && (
                          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm" data-testid="current-plan-info">
                            <div className="text-slate-500 text-xs mb-1">Plan actual</div>
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: currentPlanInfo.color || '#3B82F6' }} />
                              <span className="font-medium text-slate-900">{currentPlanInfo.nombre}</span>
                              <Badge variant="secondary" className="ml-auto">{currentPlanInfo.status}</Badge>
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>{editingInst ? 'Cambiar plan' : 'Asignar plan'}</Label>
                          <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                            <SelectTrigger data-testid="plan-select">
                              <SelectValue placeholder={plans.length ? 'Seleccionar plan…' : 'No hay planes disponibles'} />
                            </SelectTrigger>
                            <SelectContent>
                              {plans.map(plan => (
                                <SelectItem key={plan.id} value={plan.id}>
                                  {plan.nombre}{plan.codigo ? ` (${plan.codigo})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!editingInst && (
                            <p className="text-xs text-slate-500">
                              Opcional: selecciona un plan para crear la suscripción automáticamente. Si lo dejas vacío, podrás asignarlo más tarde.
                            </p>
                          )}
                        </div>

                        {selectedPlanId && (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Modalidad</Label>
                              <Select value={selectedModalidad} onValueChange={setSelectedModalidad}>
                                <SelectTrigger data-testid="plan-modality-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="mensual">Mensual</SelectItem>
                                  <SelectItem value="anual">Anual</SelectItem>
                                  <SelectItem value="unico">Pago único</SelectItem>
                                  <SelectItem value="personalizado">Personalizado</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Estado</Label>
                              <Select value={selectedSubStatus} onValueChange={setSelectedSubStatus}>
                                <SelectTrigger data-testid="plan-status-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="activa">Activa</SelectItem>
                                  <SelectItem value="prueba">Prueba</SelectItem>
                                  <SelectItem value="cortesia">Cortesía</SelectItem>
                                  <SelectItem value="pendiente">Pendiente</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}

                        {/* Comparador rápido — solo si cambia el plan */}
                        {planChanged && comparison && (
                          <div className="mt-2 p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-3" data-testid="plan-comparator">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2 text-slate-600">
                                <span className="font-medium">{comparison.currentPlan?.nombre || 'Sin plan'}</span>
                                <ArrowRight size={14} />
                                <span className="font-semibold text-slate-900">{comparison.newPlan?.nombre}</span>
                              </div>
                              {comparison.isNewAssignment && (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">Asignación inicial</Badge>
                              )}
                            </div>

                            {/* Módulos ganados */}
                            {comparison.moduleAdded.length > 0 && (
                              <div>
                                <div className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                                  <Plus size={12} weight="bold" /> Módulos que gana ({comparison.moduleAdded.length})
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {comparison.moduleAdded.slice(0, 8).map(k => (
                                    <Badge key={k} className="bg-green-100 text-green-700 text-[10px] font-normal">
                                      {MODULE_LABELS[k] || k}
                                    </Badge>
                                  ))}
                                  {comparison.moduleAdded.length > 8 && (
                                    <Badge variant="outline" className="text-[10px]">+{comparison.moduleAdded.length - 8}</Badge>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Módulos perdidos */}
                            {comparison.moduleRemoved.length > 0 && (
                              <div>
                                <div className="text-xs font-medium text-rose-700 mb-1 flex items-center gap-1">
                                  <Minus size={12} weight="bold" /> Módulos que pierde ({comparison.moduleRemoved.length})
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {comparison.moduleRemoved.slice(0, 8).map(k => (
                                    <Badge key={k} className="bg-rose-100 text-rose-700 text-[10px] font-normal">
                                      {MODULE_LABELS[k] || k}
                                    </Badge>
                                  ))}
                                  {comparison.moduleRemoved.length > 8 && (
                                    <Badge variant="outline" className="text-[10px]">+{comparison.moduleRemoved.length - 8}</Badge>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Diferencias de límites */}
                            {comparison.limitDiffs.length > 0 && (
                              <div>
                                <div className="text-xs font-medium text-slate-700 mb-1">Cambios de cupos y límites</div>
                                <div className="space-y-1">
                                  {comparison.limitDiffs.map(diff => (
                                    <div key={diff.key} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border border-slate-100">
                                      <span className="text-slate-600">{LIMIT_LABELS[diff.key] || diff.key}</span>
                                      <span className="flex items-center gap-2">
                                        <span className="text-slate-400 line-through">{diff.from}</span>
                                        <ArrowRight size={10} />
                                        <span className="font-medium text-slate-900">{diff.to}</span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {comparison.moduleAdded.length === 0 && comparison.moduleRemoved.length === 0 && comparison.limitDiffs.length === 0 && (
                              <p className="text-xs text-slate-500 italic">Sin diferencias significativas de módulos o cupos.</p>
                            )}
                          </div>
                        )}

                        {/* Historial de plan — solo al editar */}
                        {editingInst && planHistory.length > 0 && (
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => setShowHistory(v => !v)}
                              className="w-full flex items-center justify-between text-xs text-slate-600 hover:text-slate-900 py-2 border-t border-slate-200"
                              data-testid="toggle-plan-history"
                            >
                              <span className="flex items-center gap-2">
                                <ClockCounterClockwise size={14} />
                                Historial de cambios de plan ({planHistory.length})
                              </span>
                              <span className="text-slate-400">{showHistory ? '▲' : '▼'}</span>
                            </button>
                            {showHistory && (
                              <div className="mt-2 space-y-2 max-h-56 overflow-y-auto" data-testid="plan-history-list">
                                {planHistory.map(h => (
                                  <div key={h.id} className="text-xs bg-white border border-slate-100 rounded-lg p-2">
                                    <div className="flex items-center gap-2 font-medium text-slate-800">
                                      {h.from_plan_nombre ? (
                                        <>
                                          <span className="text-slate-500">{h.from_plan_nombre}</span>
                                          <ArrowRight size={11} />
                                          <span>{h.to_plan_nombre}</span>
                                        </>
                                      ) : (
                                        <>
                                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">Inicial</Badge>
                                          <span>{h.to_plan_nombre}</span>
                                        </>
                                      )}
                                      <Badge variant="outline" className="ml-auto text-[10px]">{h.to_status}</Badge>
                                    </div>
                                    <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                                      <span>
                                        {h.changed_by_nombre || h.changed_by_email || 'Sistema'}
                                      </span>
                                      <span>
                                        {h.changed_at ? new Date(h.changed_at).toLocaleString('es-CL', {
                                          day: '2-digit', month: '2-digit', year: 'numeric',
                                          hour: '2-digit', minute: '2-digit'
                                        }) : ''}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Settings Section */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-medium flex items-center gap-2 mb-4">
                    <Gear size={18} />
                    Configuración
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="max_usuarios">Máx. Usuarios</Label>
                      <Input
                        id="max_usuarios"
                        type="number"
                        min="1"
                        value={formData.settings.max_usuarios}
                        onChange={(e) => handleSettingsChange('max_usuarios', parseInt(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max_simulaciones">Máx. Simulaciones/mes</Label>
                      <Input
                        id="max_simulaciones"
                        type="number"
                        min="1"
                        value={formData.settings.max_simulaciones_mes}
                        onChange={(e) => handleSettingsChange('max_simulaciones_mes', parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="color_primario">Color Primario</Label>
                      <div className="flex gap-2">
                        <Input
                          id="color_primario"
                          type="color"
                          value={formData.settings.color_primario}
                          onChange={(e) => handleSettingsChange('color_primario', e.target.value)}
                          className="w-12 h-10 p-1"
                        />
                        <Input
                          value={formData.settings.color_primario}
                          onChange={(e) => handleSettingsChange('color_primario', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="color_secundario">Color Secundario</Label>
                      <div className="flex gap-2">
                        <Input
                          id="color_secundario"
                          type="color"
                          value={formData.settings.color_secundario}
                          onChange={(e) => handleSettingsChange('color_secundario', e.target.value)}
                          className="w-12 h-10 p-1"
                        />
                        <Input
                          value={formData.settings.color_secundario}
                          onChange={(e) => handleSettingsChange('color_secundario', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <Label htmlFor="permite_escenarios">Permite crear escenarios</Label>
                    <Switch
                      id="permite_escenarios"
                      checked={formData.settings.permite_crear_escenarios}
                      onCheckedChange={(v) => handleSettingsChange('permite_crear_escenarios', v)}
                    />
                  </div>

                  {editingInst && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <Label htmlFor="is_active">Establecimiento activo</Label>
                      <Switch
                        id="is_active"
                        checked={formData.is_active !== false}
                        onCheckedChange={(v) => handleChange('is_active', v)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseModal}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-establishment-btn">
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
